export class TimeoutPromise extends Promise {
    setDeffered(deffered) {
        this.deferred = deffered;
    }
    _cancelTimeout() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        this.timeout = null;
    }
    startTimeout(handler, ms) {
        this.timeout = setTimeout(handler, ms);
    }
}
export function promiseWithRetries(promiseFactory, maxAttempts, delayMs) {
    return new Promise((resolve, reject) => {
        const once = (counter, delay) => {
            promiseFactory().then((success) => {
                resolve(success);
            }, (error) => {
                if (counter > 0) {
                    setTimeout(() => once(counter - 1, delay * 2), delay);
                }
                else {
                    reject(error);
                }
            });
        };
        once(maxAttempts, delayMs || 0);
    });
}
const defaultTimeoutReason = (delay) => new CancelledPromiseError(`Promise timeout after ${delay} ms`);
export function deferredPromise() {
    const deferred = {};
    deferred.promise = new TimeoutPromise((resolve, reject) => {
        var _a;
        deferred.resolve = resolve;
        deferred.reject = reject;
        (_a = deferred.promise) === null || _a === void 0 ? void 0 : _a.setDeffered(deferred);
        deferred.rejectOnTimeout = (ms, reason = defaultTimeoutReason(ms)) => {
            var _a;
            (_a = deferred.promise) === null || _a === void 0 ? void 0 : _a.startTimeout(() => reject(reason), ms);
            return deferred;
        };
    }).then((res) => {
        var _a;
        (_a = deferred.promise) === null || _a === void 0 ? void 0 : _a._cancelTimeout();
        return res;
    }, (err) => {
        var _a;
        (_a = deferred.promise) === null || _a === void 0 ? void 0 : _a._cancelTimeout();
        return Promise.reject(err);
    });
    return deferred;
}
/**
 * Return a new promise that times out after some time with a rejection.
 *
 * @returns {Promise}
 */
export function makeTimeoutPromise(delay = 60000, reason = defaultTimeoutReason(delay)) {
    return deferredPromise().rejectOnTimeout(delay, reason).promise;
}
/**
 * Wrap a promise with functionality to automatically time it out
 * after some time and stop waiting for the original promise.
 */
export function promiseWithTimeout(promise, delay = 3000, reason = defaultTimeoutReason(delay)) {
    const timeoutPromise = makeTimeoutPromise(delay, reason);
    return Promise.race([
        promise.then((res) => {
            if (timeoutPromise._cancelTimeout) {
                timeoutPromise._cancelTimeout();
            }
            return res;
        }, (err) => {
            if (timeoutPromise._cancelTimeout) {
                timeoutPromise._cancelTimeout();
            }
            return Promise.reject(err);
        }),
        timeoutPromise,
    ]);
}
export class CancelledPromiseError extends Error {
}
export function createKeycloakPromise() {
    // Need to create a native Promise which also preserves the
    // interface of the custom promise type previously used by the API
    const p = {
        setSuccess: function (result) {
            p.resolve(result);
        },
        setError: function (result) {
            p.reject(result);
        },
    };
    p.promise = new Promise(function (resolve, reject) {
        p.resolve = resolve;
        p.reject = reject;
    });
    p.promise.success = function (callback) {
        this.then(function handleSuccess(value) {
            callback(value);
        });
        return this;
    };
    p.promise.error = function (callback) {
        this.catch(function handleError(error) {
            callback(error);
        });
        return this;
    };
    return p;
}
